<template>
<AbstractCard
  :product="product"
  :category="product"
  v-bind="$attrs"
  v-on="$listeners"
  :no-description="!productsSettings.display_description"
  :no-variant-labels="withoutVariantOptionLabels"
  :promotion-id="promotionId"
  :reward-id="rewardId"
  class="small"
  :style="{
    '--card-border-width': `${productsSettings.card_border_width}px`,
    '--card-shadow-size': productsSettings.card_shadow_size,
    '--card-shadow-opacity': productsSettings.card_shadow_opacity,
    '--card-hover-shadow-size': productsSettings.card_hover_shadow_size,
    '--card-hover-shadow-opacity': productsSettings.card_hover_shadow_opacity,
    'border-width': 'var(--card-border-width)',
    '--image-aspect-ratio': productsSettings.card_image_aspect_ratio,
    '--image-object-fit': productsSettings.card_image_object_fit,
  }"
  :class="styleType"
>
  <template slot="top">
    <figure class="product-card-image" :class="{ 'product-card-image--placeholder': !hasMedia }">
      <router-link :to="productRouteParams"tabindex="-1">
        <responsive-image v-if="hasMedia" :media="product.media[0]" :sizes="['large', 'medium']" :fallback="photoPlaceholder" :alt="product.name" />
        <img v-else :src="photoPlaceholder" :alt="product.name">
      </router-link>
    </figure>
  </template>
  <template slot="title">
    <router-link :to="productRouteParams">
      {{ product.name }}
    </router-link>
  </template>
  <template #beforePriceAndButtons="{ instance }">
    <p v-if="instance.variantWeight" class="product-card-small__weight">Вес: {{ instance.variantWeight }}</p>
    <div class="flex-grow"/>
  </template>
</AbstractCard>
</template>

<script>
import { mapGetters } from 'vuex'
import AbstractCard from './partials/AbstractCard'
import ResponsiveImage from '@/components/images/ResponsiveImage'

export default {
  props: {
    product: Object,

    category: Object,

    styleType: {
      type: String,
      default: 'compact',
      validator(v) {
        return ['compact', 'long', 'frameless'].includes(v);
      }
    },

    promotionId: {
      required: false,
      type: String,
      default: null,
    },

    rewardId: {
      required: false,
      type: String,
      default: null,
    },
  },

  components: {
    AbstractCard,
    ResponsiveImage,
  },

  computed: {
    ...mapGetters({
      productsSettings: 'settings/products',
      photoPlaceholder: 'ui/photoPlaceholder',
    }),

    hasMedia() {
      return Array.isArray(this.product.media) && this.product.media.length > 0
    },

    productRouteParams() {
      const params = {
        name: 'product',
        params: {
          productId: this.product.short_id,
          productSlug: this.product.slug,
          product: this.product,
          promotionId: this.promotionId,
          rewardId: this.rewardId,
        },
      }

      if (this.promotionId && this.rewardId) {
        params.query = params.query || {}
        params.query.promotion_id = this.promotionId
        params.query.reward_id = this.rewardId
      }

      return params
    },

    withoutVariantOptionLabels() {
      switch (this.productsSettings.display_variant_option_label) {
        case 'never':
          return true;
        case 'always':
        case 'products_list':
          return false;
      }

      return true;
    },
  }
}
</script>

<style lang="less">
@import "~@/assets/less/components/utilities";

@box-shadow-template: "0 0 calc(%s * 1px) 0 rgba(0, 0, 0, calc(%s * 1%))";

@hover-and-focus-selector: &:hover, &:focus-within;

.product-card.small {
  @apply relative flex flex-col;
  transition: box-shadow .1s ease-in;
  box-shadow: e(%(@box-shadow-template, var(--card-shadow-size), var(--card-shadow-opacity)));

  @{hover-and-focus-selector} {
    box-shadow: e(%(@box-shadow-template, var(--card-hover-shadow-size), var(--card-hover-shadow-opacity)));
  }

  .product-card-image {
    @apply relative;
    a {
      .aspect-ratio-image(var(--image-aspect-ratio));
      img {
        object-fit: var(--image-object-fit);
      }
    }
    &--placeholder {
      a img {
        @apply object-cover;
      }
    }
    .badges {
      @apply absolute inset-0 flex flex-col items-start pointer-events-none;
      .badge {
        @apply pointer-events-auto bg-success-light mb-1 p-1 text-xs text-white rounded-sm;
      }
    }
  }

  .product-category-title {
    @apply text-sm font-normal my-2 text-grey-dark;
  }

  .product-title {
    @apply text-lg font-normal mb-1;

    a {
      @apply text-black;
    }
  }

  .product-short-description {
    @apply text-black opacity-60 font-light mb-1;
    @font-size: theme('fontSize.sm');
    font-size: @font-size;
    @line-height: theme('lineHeight.tight');
    line-height: @line-height;
    @max-lines: 4;

    // Using both `max-width` and `line-clamp` for extended support.
    // TODO check if this needs to be updated
    max-height: calc(@max-lines * (@font-size * @line-height)); // TODO add some kind of ellipsis. Opacity gradient?
    overflow: hidden;
    display: -webkit-box;
    line-clamp: @max-lines;
    -webkit-line-clamp: @max-lines;
    -webkit-box-orient: vertical;
  }

  .product-variants-wrapper {
    @apply my-2;

    .product-variants-list {
      @apply flex flex-wrap -mr-3 -mb-2;
    }
  }

  .product-variant-selector {
    @apply flex-grow mb-2 pr-3;
    flex-basis: 50%;

    &.product-modifiers-group-component {
      @apply mb-4;
    }

    label {
      @apply block font-light text-black text-sm mb-2;

      &.font-normal {
        @apply font-normal;
      }
    }

    .v-select {
      .vs__dropdown-toggle {
        @apply border border-grey-lightest; // So it has the same size as the ones with border-primary.
      }
      .vs__dropdown-menu {
        max-height: 200px;
        @apply overflow-hidden overflow-y-scroll;
        .vs__dropdown-option {
          @apply border border-grey-lightest;
          &--selected {
            @apply text-primary border-primary;
          }
          &--highlight {
            @apply bg-primary-lighter;
            &:not(.vs__dropdown-option--selected) {
              @apply border-primary-lighter;
            }
          }
        }
      }
    }
  }

  .product-card-small__weight {
    @apply text-sm font-light text-dark my-1;
  }

  .price-and-buttons {
    &:not(.has-discounted-price) {
      @apply mt-2;
    }

    grid-gap: 0 0.5rem; // Can use margins if this isn't working out
    .quantity-controls {
      @apply text-base h-8 w-24;
      grid-area: cart-controls;

      > * {
        @apply h-full;
      }
      button {
        @apply p-0;
      }
      .product-quantity-controls-wrapper {
        @apply bg-primary text-white;
        .btn {
          @apply h-full text-base text-white;
          .fal {
            @apply flex justify-center; // To perfectly center the signs. TODO apply to all `.fal`s?
          }
        }
        input {
          @apply text-white font-bold;
        }
      }
      .product-cart-controls-wrapper {
        .distorted-button {
          @apply w-full h-full;
          .button__bg {
            @apply p-0;
          }
          // Adjust bubble effect positions with button width change.
          .circle {
            width: 15px;
            height: 15px;
            &.top-left {
              top: 46%;
              left: 31%;
            }
            &.bottom-right {
              bottom: 46%;
              right: 31%;
            }
          }
        }
        button {
          @apply text-sm font-medium;
        }
      }
    }
  }

  // Style types.
  &.compact {
    @apply rounded-sm p-4;
    .product-card-image {
      @apply mb-3;
      a {
        img {
          @apply rounded-sm;
        }
      }
    }
    .badges {
      @apply p-1;
    }
  }
  // &.long {}
  &.frameless {
    @card-border-radius: theme('borderRadius.lg');
    @apply p-3 rounded-lg
      border-grey-lighter; // Because border-width may be set from the designer.
    .product-card-image {
      @apply -mx-3 -mt-3 mb-2;
      img {
        // If we were to set it to `@card-border-radius`, there would be a gap.
        // Setting `overflow: hidden` would prevent variant select dropdowns from overflowing too.
        @border-radius: calc(@card-border-radius - var(--card-border-width));
        border-top-left-radius: @border-radius;
        border-top-right-radius: @border-radius;
      }
    }
    .badges {
      @apply pt-3;
      .badge {
        @apply rounded-l-none;
      }
    }
  }
}

@screen lg {
  .product-card.small {
    @hide-on-not-hover: .btn-favorite, .quantity-controls:not(.in-cart):not(.being-added-to-cart);
    @{hover-and-focus-selector} {
      @{hide-on-not-hover} {
        @apply opacity-100;
      }
    }
    &:not(:hover) {
      @{hide-on-not-hover} {
        .fade-enter-active {
          // When the product is being removed from the cart and its cart is unhovered, the transiton would play and
          // the 'add to cart' button would flash for just a moment, then would get faded out.
          // This fixes it.
          @apply opacity-0;
          transition-duration: 0;
        }
      }
    }

    @{hide-on-not-hover} {
      @apply opacity-0;
      transition: opacity .3s ease-in;
    }
  }
}
</style>
